import {
  faArrowCircleDown,
  faArrowCircleUp,
  faCheckCircle,
  faPlusCircle,
  faTrashAlt,
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import React, { FC, useCallback } from 'react'

import useSanityImage from '../../../../../../../lib/sanity/hooks/useSanityImage'
import { Product } from '../../../../../../../lib/sanity-dispensaries/manage/featured-products'
import Link from '../../../../../../global/Link'
import * as Styled from './styled'

import productImagePlaceholder from '../../../../../../../img/product-image-placeholder.png'

export interface ProductListEntrySelectedActions {
  onUp?: (product: Product) => void
  onDown?: (product: Product) => void
  onRemove: (product: Product) => void
}

export interface ProductListEntrySuggestedActions {
  onAdd: (product: Product) => void
  onRemove: (product: Product) => void
  added: boolean
}

export interface ProductListEntryProps {
  className?: string
  product: Product
  actions?:
    | ({ _tag: 'selected' } & ProductListEntrySelectedActions)
    | ({ _tag: 'suggested' } & ProductListEntrySuggestedActions)
}

const ProductListEntryActionsSelected: FC<
  { product: Product } & ProductListEntrySelectedActions
> = ({ product, onUp, onDown, onRemove }) => {
  const onClickUp = useCallback(() => (onUp ? onUp(product) : undefined), [onUp, product])
  const onClickDown = useCallback(() => (onDown ? onDown(product) : undefined), [onDown, product])
  const onClickRemove = useCallback(() => onRemove(product), [onRemove, product])

  return (
    <Styled.ButtonContainer>
      {onUp && <FontAwesomeIcon className="icon" icon={faArrowCircleUp} onClick={onClickUp} />}
      {onDown && (
        <FontAwesomeIcon className="icon" icon={faArrowCircleDown} onClick={onClickDown} />
      )}
      <FontAwesomeIcon className="icon" icon={faTrashAlt} onClick={onClickRemove} />
    </Styled.ButtonContainer>
  )
}

const ProductListEntryActionsSuggested: FC<
  { product: Product } & ProductListEntrySuggestedActions
> = ({ product, onAdd, onRemove, added }) => {
  const onClickAdd = useCallback(() => onAdd(product), [onAdd, product])
  const onClickRemove = useCallback(() => onRemove(product), [onRemove, product])

  return (
    <Styled.ButtonContainer>
      {added ? (
        <FontAwesomeIcon className="icon green" icon={faCheckCircle} onClick={onClickRemove} />
      ) : (
        <FontAwesomeIcon className="icon" icon={faPlusCircle} onClick={onClickAdd} />
      )}
    </Styled.ButtonContainer>
  )
}

export const ProductListEntry = React.forwardRef<HTMLDivElement, ProductListEntryProps>(
  function ProductListEntry({ className, product, actions, ...rest }, ref) {
    const sanityImageUrl = useSanityImage(
      { sourceJson: product.sanityImageJson || undefined },
      {
        dpr: 2,
        fit: 'max',
        // set width only to avoid crop bug: https://github.com/sanity-io/image-url/issues/32
        width: 180,
      },
    )
    return (
      <Styled.ProductListEntry className={className} ref={ref} {...rest}>
        <Styled.GapContainer>
          <Styled.ItemDetails>
            <Link to={`/products/${product.urlKey}`}>
              <Styled.ItemImage
                alt={product.name}
                src={sanityImageUrl || productImagePlaceholder}
              />
            </Link>
            <Styled.Container>
              <Styled.ItemProductLink urlKey={product.urlKey}>
                <Styled.ItemName>
                  <Styled.ItemSku>SKU: {product.sku}</Styled.ItemSku>
                  {product.name}
                </Styled.ItemName>
              </Styled.ItemProductLink>
              <Styled.ItemAttribute>
                {(product.shortDescription || {}).text || ''}
              </Styled.ItemAttribute>
            </Styled.Container>
          </Styled.ItemDetails>
          {actions?._tag === 'selected' && (
            <ProductListEntryActionsSelected product={product} {...actions} />
          )}
          {actions?._tag === 'suggested' && (
            <ProductListEntryActionsSuggested product={product} {...actions} />
          )}
        </Styled.GapContainer>
      </Styled.ProductListEntry>
    )
  },
)
